import {
  Box,
  Button,
  Flex,
  Group,
  Loader,
  LoadingOverlay,
  Paper,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconFileTypeCsv,
  IconFileTypeZip,
  IconListDetails,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router";
import { paths } from "../../shared/configs/paths";
import { ArticleErrors, ArticleResultsTable } from "../../entities/articles/ui";
import { useGate, useUnit } from "effector-react";
import { articleDetailsEntityModel } from "../../entities/articles/model/details";
import styles from "./styles.module.scss";
import { ArticleStatus } from "../../entities/articles/types";

export const ArticlesDetails = () => {
  const params = useParams();
  const navigation = useNavigate();
  const [data, isLoading] = useUnit([
    articleDetailsEntityModel.$data,
    articleDetailsEntityModel.$isLoading,
  ]);

  useGate(articleDetailsEntityModel.Gate, {
    articleId: String(params.id),
  });

  return (
    <Box p="md" pt="lg" className={styles.articleDetailsContainer}>
      <Group
        justify="space-between"
        align="flex-start"
        wrap="nowrap"
        pos={"relative"}
      >
        <Flex gap="md" align="center">
          <Title order={1}>Search Results</Title>

          <ArticleErrors />
        </Flex>

        <Flex gap="md">
          <Button
            disabled={Number(data?.similarities.length) === 0}
            variant="default"
            size="md"
            leftSection={<IconFileTypeZip />}
            onClick={() => articleDetailsEntityModel.downloadScreenshotsFx()}
          >
            Screenshots
          </Button>
          <Button
            disabled={Number(data?.similarities.length) === 0}
            variant="default"
            size="md"
            leftSection={<IconFileTypeCsv />}
            onClick={() => articleDetailsEntityModel.exportToCsvFx()}
          >
            CSV
          </Button>
          <Button
            variant="default"
            size="md"
            leftSection={<IconArrowLeft />}
            onClick={() =>
              navigation(
                data?.article.campaignId
                  ? paths.campaignDetails.replace(
                      ":id",
                      data?.article.campaignId
                    )
                  : paths.home
              )
            }
          >
            Back to Tasks
          </Button>
        </Flex>
      </Group>

      <>
        <Paper mt="md" p="md" radius="md">
          {data?.article.title ? (
            <Stack gap={0}>
              <Text size="sm">{data?.article.date && data?.article.date}</Text>
              <Flex align="center" gap="xs">
                <Text c="blue" size="lg" fw="500">
                  <a style={{ width: "100%" }} href={data?.article.originalUrl}>
                    {data?.article.title}
                  </a>
                </Text>
              </Flex>
              <Text>{data?.article.snippet && data?.article.snippet}</Text>
              <Flex justify="flex-end" gap="xs">
                <Text size="sm" fw="500" c="gray.5">options: {data?.article?.options ? JSON.parse(data?.article?.options) : "none" }</Text>
              </Flex>
            </Stack>
          ) : !data?.article.title && data?.article.status === "failed" ? (
            <Text fw={500} c={"red"}>
              Title extracting failed
            </Text>
          ) : !isLoading ? (
            <Group gap={6}>
              <Text fw={500}>Title extracting in process</Text>
              <Loader size={"xs"} />
            </Group>
          ) : (
            <Skeleton height={100} maw={300} visible={isLoading} />
          )}
        </Paper>

        {Number(data?.similarities.length) > 0 && !isLoading && (
          <>
            <Stack mt="lg">
              <ArticleResultsTable />
            </Stack>
          </>
        )}

        {Number(data?.similarities.length) === 0 && !isLoading && (
          <Paper mt="lg" p="md" radius="md">
            <Stack
              align="center"
              justify="center"
              h="300px"
              flex={1}
              w={"100vw"}
            >
              <ThemeIcon size={52} radius="sm" variant="light" color="blue">
                <IconListDetails />
              </ThemeIcon>
              <Text size="sm" fw={500}>
                {data?.article.status === ArticleStatus.pending
                  ? "No results found yet"
                  : data?.article.status === ArticleStatus.failed &&
                    !data.article.title
                  ? "No article title"
                  : data?.article.status === ArticleStatus.failed &&
                    !data.article.date
                  ? "No article date"
                  : "No results found"}
              </Text>
              <Text size="sm" c="dimmed">
                {data?.article.status === ArticleStatus.pending
                  ? "The results will be displayed here after the processing."
                  : data?.article.status === ArticleStatus.failed &&
                    !data.article.title
                  ? "Article title extracting failed, please check the article URL"
                  : data?.article.status === ArticleStatus.failed &&
                    !data.article.date
                  ? "Article date extracting failed, please try to set search date manually"
                  : "No similar articles found"}
              </Text>
            </Stack>
          </Paper>
        )}

        {isLoading && (
          <Stack flex={1} mih={60}>
            <Stack mih={60} pos={"fixed"} top={0} left={0} right={0} bottom={0}>
              <LoadingOverlay
                visible={isLoading}
                zIndex={100}
                h={"100vh"}
                overlayProps={{
                  radius: "sm",
                  blur: 5,
                  backgroundOpacity: 0.5,
                }}
                loaderProps={{ type: "bars" }}
              />
            </Stack>
          </Stack>
        )}
      </>
    </Box>
  );
};
